import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useFile } from "../context/FileContext";
import { useAuth } from "../context/AuthContext";
import { fetchOperationsContext } from "./services/marketOpsApi";

const MarketOpsContext = createContext(null);
export function MarketOpsProvider({ children }) {
  const { currentProject, isDirty } = useFile();
  const { accessToken } = useAuth();
  const projectId = currentProject?.id;
  const [assetId, setAssetId] = useState("");
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    setAssetId("");
  }, [projectId]);
  useEffect(() => {
    const controller = new AbortController();
    setResource(null);
    setError(null);
    if (!projectId || !accessToken) {
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    fetchOperationsContext({ projectId, accessToken, signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setResource({ projectId, data });
      })
      .catch((err) => { if (!controller.signal.aborted) setError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [projectId, currentProject?.updated_at, accessToken, revision]);
  const data = resource && resource.projectId === projectId ? resource.data : null;
  const value = useMemo(() => {
    const assets = data?.assets || [];
    const eligible = assets.filter((a) => a.eligible);
    const asset = eligible.find((a) => a.id === assetId) || eligible[0] || null;
    return {
      projectId, projectName: currentProject?.name, isDirty, assets, eligibleAssets: eligible,
      asset, setAssetId, resources: data?.resources || {}, issues: data?.issues || [],
      loading, error, loaded: Boolean(data), authenticated: Boolean(accessToken),
      refresh: () => setRevision((v) => v + 1),
    };
  }, [data, assetId, projectId, currentProject?.name, isDirty, loading, error, accessToken]);
  return <MarketOpsContext.Provider value={value}>{children}</MarketOpsContext.Provider>;
}
export function useMarketOps() {
  const value = useContext(MarketOpsContext);
  if (!value) throw new Error("Operations context is unavailable.");
  return value;
}
